import React, { useCallback, useEffect, useRef, useState } from 'react';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import styles from './streaming-credentials.module.css';
import GetSessionRtmp from '@/connection/streams/get-session-rtmp';
import { niceTime } from '@/utils/dates';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import { useAppSelector } from '@/store/hooks';
import { selectWorkingSession } from '@/store/slices/project/selectors';

interface StreamingCredentialsProps {
  isEditingInfo: boolean;
  disabled?: boolean;
}

export default function StreamingCredentials({ disabled, isEditingInfo }: StreamingCredentialsProps) {
  const session = useAppSelector(selectWorkingSession);
  const { language = 'en' } = useParams();
  const [rtmpInfo, setRtmpInfo] = useState({
    rtmpsAddress: '',
    streamKey: '',
    expiration: '',
  });
  const [loadingRtmps, setLoadingRtmps] = useState(true);
  const [copiedContent, setCopiedContent] = useState({ rtmpsAddress: false, streamKey: false });
  const copyRtmpsAddressTimeout = useRef<NodeJS.Timeout | null>(null);
  const copyStreamKeyTimeout = useRef<NodeJS.Timeout | null>(null);

  const revealRtmps = useCallback(async () => {
    if (!session) return;
    try {
      const { rtmps_address, stream_key, expire_at } = await GetSessionRtmp({
        sessionUuid: session.uuid,
        language: session.streaming_options.single_stream ? session.default_language : language,
      });

      setRtmpInfo({
        rtmpsAddress: rtmps_address,
        streamKey: stream_key,
        expiration: niceTime(expire_at),
      });
    } catch (error) {
      console.error('Error getting session rtmp keys', error);
    } finally {
      setLoadingRtmps(false);
    }
  }, [language, session]);

  useEffect(() => {
    if (isEditingInfo) {
      if (!rtmpInfo.rtmpsAddress) {
        revealRtmps();
      }
    }
  }, [isEditingInfo, revealRtmps, rtmpInfo.rtmpsAddress]);

  const copyRtmpsAddress = () => {
    if (!rtmpInfo.rtmpsAddress) return;

    if (copyRtmpsAddressTimeout.current) {
      clearTimeout(copyRtmpsAddressTimeout.current);
    }

    navigator.clipboard.writeText(rtmpInfo.rtmpsAddress);

    setCopiedContent((prev) => ({ ...prev, rtmpsAddress: true }));

    copyRtmpsAddressTimeout.current = setTimeout(() => {
      setCopiedContent((prev) => ({ ...prev, rtmpsAddress: false }));
    }, 2000);
  };

  const copyPassword = () => {
    if (!rtmpInfo.streamKey) return;

    if (copyStreamKeyTimeout.current) {
      clearTimeout(copyStreamKeyTimeout.current);
    }

    navigator.clipboard.writeText(rtmpInfo.streamKey);

    setCopiedContent((prev) => ({ ...prev, streamKey: true }));

    copyStreamKeyTimeout.current = setTimeout(() => {
      setCopiedContent((prev) => ({ ...prev, streamKey: false }));
    }, 2000);
  };

  return (
    <div data-component="StreamingCredentials">
      <Container
        background="var(--gray-900)"
        className={classNames(styles.streamingCredentials, 'grid gap-8 ai-center grid-template-columns-2')}
        borderRadius="12px"
        height="86px"
        inert={disabled} /* prevent elements from being focasble when not in editing mode */
      >
        <>
          <Container background="var(--gray-900)" className="flex fd-column gap-4">
            <Text className="L2 margin-l-12" color="var(--gray-400)">
              RTMP Address
            </Text>
            <div className={styles['mockInput']} style={{ border: '1px solid var(--gray-600)' }}>
              <Text
                tag="label"
                className="P2 break-all"
                color="var(--gray-100)"
                clamp={1}
                width={'calc(100% - 16px)'}
                height={!rtmpInfo.rtmpsAddress ? '16px' : undefined}
              >
                {rtmpInfo.rtmpsAddress}
              </Text>
              <Button
                onClick={copyRtmpsAddress}
                disabled={disabled || loadingRtmps || !rtmpInfo.rtmpsAddress}
                hoverBackgroundColor="var(--gray-650)"
                className="padding-4 square-24 radius-full flex ai-center jc-center"
                loading={loadingRtmps}
              >
                <Text opacity={loadingRtmps ? 0 : 1}>
                  <Icon name={copiedContent.rtmpsAddress ? ICONS.CHECK : ICONS.COPY_FILLED} size={12} />
                </Text>
              </Button>
            </div>
          </Container>
          <Container background="var(--gray-900)" className="flex fd-column gap-4">
            <Text className="L2 margin-l-12" color="var(--gray-400)">
              Stream Key
            </Text>
            <div className={styles['mockInput']} style={{ border: '1px solid var(--gray-600)' }}>
              <Text
                tag="label"
                className="P2 break-all"
                color="var(--gray-100)"
                clamp={1}
                width={'calc(100% - 16px)'}
                height={!rtmpInfo.streamKey ? '16px' : undefined}
              >
                {rtmpInfo.streamKey}
              </Text>
              <Button
                onClick={copyPassword}
                disabled={disabled || loadingRtmps || !rtmpInfo.streamKey}
                hoverBackgroundColor="var(--gray-650)"
                className="padding-4 square-24 radius-full flex ai-center jc-center"
                loading={loadingRtmps}
              >
                <Text opacity={loadingRtmps ? 0 : 1}>
                  <Icon name={copiedContent.streamKey ? ICONS.CHECK : ICONS.COPY_FILLED} size={12} />
                </Text>
              </Button>
            </div>
          </Container>
        </>
      </Container>
    </div>
  );
}
